import {
  Component,
  EventEmitter,
  HostListener,
  OnInit,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from "@angular/core";
import Highcharts from "highcharts";
import { PharmaDataService } from "../pharma-data.service";
import { AppService } from "src/app/app.service";
import { Subscription } from "rxjs";
import { Store } from "@ngrx/store";
import { FilterEventChanged } from "src/app/store/actions/pharma-page.actions";
import { AppState, stateGetter } from "src/app/store/state/app.state";

import {
  ColumnChartOptions,
  DrugsByIndicationApiResponse,
  ChartStateColors,
  GlobalHighchartOptions,
} from "../pharma-page.model";
import { VAIconRegistryService, infoCircle } from "@va/core-library/icons";

@Component({
  selector: "app-drugs-by-indication-bar-chart",
  templateUrl: "./drugs-by-indication-bar-chart.component.html",
  styleUrls: ["./drugs-by-indication-bar-chart.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class DrugsByIndicationBarChartComponent implements OnInit, OnDestroy {
  chartFilters;
  facetFilters;
  pillsData;
  highcharts = Highcharts;
  chartIndicationOptions: Highcharts.Options;
  chartReady = false;
  showError = false;
  errorMessage = "";
  updateIndicationFlag = false;
  chart: Highcharts.Chart;
  totalCount = 0;
  tickersInfo: any;
  selectedRank = 0;
  selectedIndication;
  lastCalled;
  categoryToIdMap = {};
  dataCallSubscription: Subscription;
  pharmaDatabasePageSubscriber: Subscription;
  @Output() onChartInstance: EventEmitter<Highcharts.Chart> =
    new EventEmitter();

  constructor(
    private service: PharmaDataService,
    private appService: AppService,
    private store: Store<AppState>,
    private iconRegistryService: VAIconRegistryService,
  ) {
    this.iconRegistryService.registerIcons([infoCircle]);
  }

  @HostListener("window:resize", [])
  sizeChange() {
    this.updateIndicationFlag = true;
    if (this.chart) {
      setTimeout(() => {
        this.onChartInstance.emit(this.chart);
      }, 50);
    }
  }

  ngOnInit() {
    Highcharts.setOptions(GlobalHighchartOptions);
    this.pharmaDatabasePageSubscriber = this.store
      .select(stateGetter("pharmaDatabasePage"))
      .subscribe((data: any) => {
        this.chartFilters = data.chartFilters;
        this.facetFilters = data.facetFilters;
        this.pillsData = data.pillsData;
        if (
          data.filterEvent.filterChanged &&
          data.filterEvent.filterChanged === "chart" &&
          !data.filterEvent.source.includes("indication")
        ) {
          this.facetFilters = data.lastFacetFilter;
        }
        if (
          data.filterEvent.filterChanged &&
          data.filterEvent.filterChanged !== "grid" &&
          data.filterEvent.source !== "indication-selected"
        ) {
          this.chartReady = false;
          this.getChartData();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.facetFilters && changes.facetFilters.currentValue) ||
      (changes.chartFilters &&
        changes.chartFilters.currentValue &&
        (this.callGetChartData(changes.chartFilters.currentValue) ||
          changes.chartFilters.isFirstChange()))
    ) {
      this.chartReady = false;
      this.getChartData();
    }
  }
  callGetChartData(currentChartFilters) {
    if (
      this.lastCalled &&
      this.lastCalled.chart_filter.indication === "" &&
      this.selectedRank > 10
    ) {
      return true;
    }
    if (
      this.lastCalled &&
      this.appService.compare(currentChartFilters, this.lastCalled.chart_filter)
    ) {
      return false;
    }

    return true;
  }
  getChartData() {
    const params = {
      chart_filter: { ...this.chartFilters },
      filter: { ...this.facetFilters },
    };
    this.lastCalled = params;
    if (this.dataCallSubscription) {
      this.dataCallSubscription.unsubscribe();
    }
    this.dataCallSubscription = this.service
      .getDrugsByIndication(params)
      .subscribe((response: DrugsByIndicationApiResponse) => {
        if (response.status === "success") {
          this.convertDataToChartObj(response.data);
          this.totalCount = response.data.total;
          this.selectedRank = response.data.selected_indication_rank;
          this.selectedIndication = params.chart_filter.indication;
          this.showError = false;
          this.categoryToIdMap = response.data["values_mtag_map"];
        } else {
          this.errorMessage = response.message;
          this.showError = true;
        }

        setTimeout(() => {
          this.chartReady = true;
        }, 0);
      });
  }

  convertDataToChartObj(dataObj) {
    const categories = dataObj.values.map((val) => val[0]);
    const series = dataObj.values;
    this.chartIndicationOptions =
      this.appService.superClone(ColumnChartOptions);
    this.chartIndicationOptions.xAxis["categories"] = categories;
    this.chartIndicationOptions.series[0]["data"] =
      this.setSelectedOnLoad(series);
    this.chartIndicationOptions.plotOptions.series.events = {
      click: (event) => this.clickOnChart(event),
    };
  }

  clickOnChart(data) {
    if (data.point.y === 0) {
      //Block 0 count click on chart
      return;
    }
    const id = this.categoryToIdMap[data.point.category];
    if (id === this.chartFilters.indication) {
      this.chartFilters.indication = "";
      this.lastCalled.chart_filter.indication = "";

      this.chartIndicationOptions.plotOptions.series.color =
        ChartStateColors.selected;
      //deselect all
      const series = this.chartIndicationOptions.series[0]["data"];
      this.chartIndicationOptions.series[0]["data"] = series.map((val) => {
        if (val.selected) val.selected = false;
        return val;
      });
      this.updateIndicationFlag = true;
      this.pillsData = {
        ...this.pillsData,
        extraInfo: { ...this.pillsData.extraInfo, id: data.point.category },
      };
      this.store.dispatch(
        new FilterEventChanged({
          filterChanged: "chart",
          source: "indication-deselected",
          data: {
            chartFilters: this.chartFilters,
            extraInfo: this.pillsData.extraInfo,
          },
        }),
      );
      return;
    }
    //update filter and emit event for other charts
    this.lastCalled.chart_filter.indication = id;
    this.chartFilters = JSON.parse(
      JSON.stringify({
        ...this.chartFilters,
        indication: id,
      }),
    );

    //change color of unselected bars
    this.store.dispatch(
      new FilterEventChanged({
        filterChanged: "chart",
        source: "indication-selected",
        data: {
          chartFilters: this.chartFilters,
          extraInfo: {
            indication_data: {
              [id]: data.point.category,
            },
          },
        },
      }),
    );
    //change color of unselected bars
    this.chartIndicationOptions.plotOptions.series.color =
      ChartStateColors.deselected;
    this.updateIndicationFlag = true;
  }
  setSelectedOnLoad(series) {
    if (this.chartFilters.indication) {
      const newSeries = series.map((val, index) => {
        const obj = {
          y: val[1],
          x: index,
          selected: false,
        };
        const id = this.categoryToIdMap[val[0]];
        if (id === this.chartFilters.indication) {
          obj.selected = true;
          return obj;
        }
        return obj;
      });
      this.chartIndicationOptions.plotOptions.series.color =
        ChartStateColors.deselected;

      return newSeries;
    }
    return series;
  }

  ngOnDestroy() {
    if (this.dataCallSubscription) {
      this.dataCallSubscription.unsubscribe();
    }
    if (this.pharmaDatabasePageSubscriber) {
      this.pharmaDatabasePageSubscriber.unsubscribe();
    }
  }
}
